import { useCallback, useEffect, useRef, useState } from "react";
import {
  GoogleMap,
  useJsApiLoader,
  MarkerClusterer,
  Libraries,
} from "@react-google-maps/api";
import { Constants } from "constants/Constants";
import { Point } from "./Point.type";
import { generatedMapStyle, ZoomLevel } from "./MapStyle";
import MapMarker from "./MapMarker";
import { generateClusterMarkerIcon } from "./ClusterMarker";

const CLUSTER_COLORS = {
  gold: "#FFD700",
  orange: "#ffa500",
  red: "#ff0000",
};

const CLUSTER_STYLES = [
  generateClusterMarkerIcon(CLUSTER_COLORS.gold),
  generateClusterMarkerIcon(CLUSTER_COLORS.orange),
  generateClusterMarkerIcon(CLUSTER_COLORS.red),
];

type MapProps = {
  points?: Point[];
  height?: number;
  onMarkerPress?: (e: any) => void;
  zoomLvl?: ZoomLevel;
  initialRegion?: {
    id: number | string;
    lat: number;
    lng: number;
  };
  selectedPointId?: string | number;
  setSelectedPointId?: (id: string | number) => void;
  scrollEnabled?: boolean;
  centeredOnUser?: boolean;
  forUserAreaOfInterest?: boolean;
  areaOfInterest?: any;
};

export type MapRef = {
  drawPolygon: () => void;
};

const libraries: Libraries = ["geometry", "drawing"];

function MapContainer({
  points = [],
  height,
  onMarkerPress,
  selectedPointId,
  setSelectedPointId,
  scrollEnabled = true,
  zoomLvl,
  centeredOnUser = false,
  forUserAreaOfInterest = false,
  initialRegion,
  areaOfInterest = null,
}: MapProps) {
  // const { theme } = useTheme();
  const mapRef = useRef<google.maps.Map | null>(null);
  const [initialMapCenter, setInitialMapCenter] = useState<any>(null);

  const { isLoaded } = useJsApiLoader({
    id: "google-map-script",
    googleMapsApiKey: "AIzaSyBoabCFYUx-SQW3QtY0WmkRRuzsfhmQtCs",
    libraries,
    version: "3.55", // Use a stable version instead of weekly
  });

  useEffect(() => {
    if (initialRegion) {
      setInitialMapCenter(initialRegion);
    } else {
      // Simple default center - let fitBounds handle the final positioning
      setInitialMapCenter({
        lat: parseFloat(Constants.MAP_DEFAULT_LAT),
        lng: parseFloat(Constants.MAP_DEFAULT_LAT),
      });
    }
  }, [
    centeredOnUser,
    points,
    areaOfInterest,
    forUserAreaOfInterest,
    initialRegion,
  ]);

  const mapContainerWebStyle = {
    height: height ? `${height}px` : "100%",
    width: "100%",
  };

  /*  const zoomAroundPoints = useMemo(() => {
    if (forUserAreaOfInterest && areaOfInterest?.length > 1) {
      let minLat = areaOfInterest[0].lat;
      let maxLat = areaOfInterest[0].lat;
      let minLng = areaOfInterest[0].lng;
      let maxLng = areaOfInterest[0].lng;

      areaOfInterest.forEach((point: Point) => {
        minLat = Math.min(minLat, point.lat);
        maxLat = Math.max(maxLat, point.lat);
        minLng = Math.min(minLng, point.lng);
        maxLng = Math.max(maxLng, point.lng);
      });

      const latDelta = maxLat - minLat;
      const lngDelta = maxLng - minLng;

      return Math.min(
        Math.log2(360 / lngDelta) + 1, // Largeur du monde (360 degrés) divisée par la différence de longitudes
        Math.log2(180 / latDelta) + 1 // Hauteur du monde (180 degrés) divisée par la différence de latitudes
      );
    }
    if (points?.length > 1) {
      let minLat = points[0].lat;
      let maxLat = points[0].lat;
      let minLng = points[0].lng;
      let maxLng = points[0].lng;

      points.forEach((point) => {
        minLat = Math.min(minLat, point.lat);
        maxLat = Math.max(maxLat, point.lat);
        minLng = Math.min(minLng, point.lng);
        maxLng = Math.max(maxLng, point.lng);
      });

      const latDelta = maxLat - minLat;
      const lngDelta = maxLng - minLng;

      return Math.min(
        Math.log2(360 / lngDelta) + 1, // Largeur du monde (360 degrés) divisée par la différence de longitudes
        Math.log2(180 / latDelta) + 1 // Hauteur du monde (180 degrés) divisée par la différence de latitudes
      );
    }
    return zoomLvl || 14;
  }, [forUserAreaOfInterest, areaOfInterest, points, zoomLvl]);
 */

  // Function to fit bounds with smooth animation
  const fitBoundsWithAnimation = useCallback(
    (map: any, pointsToFit: Point[]) => {
      if (pointsToFit.length === 0 || centeredOnUser) return;

      if (pointsToFit.length === 1) {
        // Single point - center directly on it
        setTimeout(() => {
          map.setCenter(pointsToFit[0]);
          map.setZoom(12);
        }, 500);
      } else {
        // Delay fitBounds to ensure all markers are rendered
        setTimeout(() => {
          // Create visual bounds that include marker icon areas
          const visualBounds = new google.maps.LatLngBounds();

          pointsToFit.forEach((point) => {
            // Extend bounds to include the visual marker area
            const markerLatLng = new google.maps.LatLng(point.lat, point.lng);
            visualBounds.extend(markerLatLng);
          });

          // First fitBounds to get optimal view
          map.fitBounds(visualBounds, 100);

          // Then smoothly zoom to maximum level where all points remain visible
          setTimeout(() => {
            const currentZoom = map.getZoom();
            const maxZoom = Math.min(18, currentZoom + 2); // Zoom in 2 levels or to max 18

            // Smooth zoom animation
            const zoomStep = maxZoom > currentZoom ? 1 : -1;
            let targetZoom = currentZoom;

            const animateZoom = () => {
              if (
                (zoomStep > 0 && targetZoom < maxZoom) ||
                (zoomStep < 0 && targetZoom > maxZoom)
              ) {
                targetZoom += zoomStep;
                map.setZoom(targetZoom);

                // Check if all points are still in view after zoom
                const bounds = map.getBounds();
                const allVisible = pointsToFit.every((point) => {
                  return bounds?.contains(
                    new google.maps.LatLng(point.lat, point.lng)
                  );
                });

                if (!allVisible) {
                  // Zoom back out one level if any point goes out of view
                  map.setZoom(targetZoom - zoomStep);
                  return;
                }

                // Continue animation with slight delay for smooth effect
                setTimeout(animateZoom, 150);
              }
            };

            animateZoom();
          }, 500); // Wait for initial fitBounds to complete
        }, 100); // Small delay to ensure markers are rendered
      }
    },
    [centeredOnUser]
  );

  const onLoad = useCallback(
    (map: any) => {
      // Fit bounds on initial load
      if (!mapRef.current) {
        mapRef.current = map;
        fitBoundsWithAnimation(map, points);
      }
    },
    [points, fitBoundsWithAnimation]
  );

  // Keep track of previous points length
  const prevPointsLengthRef = useRef(points.length);

  // Fit bounds whenever points change (for filtering)
  useEffect(() => {
    if (!mapRef.current) return;

    // Only run fitBounds if the points array length changed (filtering)
    if (points.length !== prevPointsLengthRef.current) {
      fitBoundsWithAnimation(mapRef.current, points);
    }

    prevPointsLengthRef.current = points.length;
  }, [points, fitBoundsWithAnimation]);

  return (
    <div style={{ width: "100%", height: "100%" }}>
      {isLoaded ? (
        <GoogleMap
          mapContainerStyle={mapContainerWebStyle}
          center={initialMapCenter || { lat: 43.6043, lng: 1.4437 }}
          zoom={centeredOnUser ? zoomLvl || 14 : undefined} // Let fitBounds control zoom when not centering on user
          options={
            {
              styles: [
                ...generatedMapStyle,
                {
                  featureType: "poi",
                  elementType: "labels",
                  stylers: [{ visibility: "off" }],
                },
                {
                  featureType: "poi.park",
                  elementType: "geometry",
                  stylers: [{ visibility: "on" }],
                },
                {
                  featureType: "transit.station.airport",
                  elementType: "geometry",
                  stylers: [{ visibility: "on" }],
                },
                {
                  featureType: "transit.station.airport",
                  elementType: "labels",
                  stylers: [{ visibility: "off" }],
                },
              ],
              streetViewControl: false,
              fullscreenControl: false,
              gestureHandling: scrollEnabled ? "auto" : "none",
              zoomControl: false,
              mapTypeControl: false,
              clickableIcons: false,
            } as unknown as google.maps.MapOptions
          }
          onLoad={onLoad}
          onZoomChanged={() => {
            // Zoom level tracking for debugging (can be removed in production)
            const zoom = mapRef.current?.getZoom();
            if (process.env.NODE_ENV === "development") {
              console.info("Zoom level:", zoom);
            }
          }}
          onClick={() => {
            console.log("Map clicked");
            // Déselectionner le point
            setSelectedPointId?.("");
          }}
        >
          <MarkerClusterer
            options={{
              zoomOnClick: true,
              maxZoom: 9,
              gridSize: 100,
              minimumClusterSize: 1,
              styles: CLUSTER_STYLES,
            }}
          >
            {(clusterer) => (
              <div>
                {points.map((point) => {
                  return (
                    <MapMarker
                      key={point?.id}
                      point={point}
                      selectedPointId={selectedPointId}
                      setSelectedPointId={setSelectedPointId}
                      onMarkerPress={onMarkerPress}
                      mapRef={mapRef}
                      clusterer={clusterer}
                    />
                  );
                })}
              </div>
            )}
          </MarkerClusterer>
        </GoogleMap>
      ) : (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100%",
          }}
        >
          Loading map...
        </div>
      )}
    </div>
  );
}

export default MapContainer;
